import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import useBundleTranslation from 'i18n';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import IconMi from 'components/common/icon/IconMi';
import { FormComponentBuilder } from 'components/common/form/layout';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useQueryClient } from '@tanstack/react-query';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { useFormState } from 'react-hook-form';
import { instance } from 'api/api';

interface RestoreDefaultsFormButtonProps extends FormControlProps {
    url: string;
    reloadGrids: string[];
    reloadFields: string[];
}

export default function RestoreDefaultsFormButton({
    controlProps,
    elementProps,
}: FormElementControlPropsType<RestoreDefaultsFormButtonProps>) {
    const { t } = useBundleTranslation(elementProps?.translationNS);
    const [isLoading, toggleLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const { isDirty } = useFormState({ control: controlProps.form.hookFormControl });
    const queryClient = useQueryClient();
    const defaultValues = controlProps.form.hookFormGetValues('default_values');

    const handleClick = async () => {
        setShowConfirm(true);
    };

    const confirmSettings: PopupSettings = {
        title: t('button_restore.confirmation_header'),
        textOK: t('button_restore.confirmation_button'),
        noOk: isDirty,
    };

    const onConfirmed = () => {
        toggleLoading(true);
        instance
            .get(controlProps.url)
            .then(() => {
                if (controlProps.reloadFields.length > 0) {
                    controlProps.reloadFields.forEach((field) => {
                        const defaultValue = defaultValues[field];
                        controlProps.form.hookFormSetValue(field, defaultValue, {
                            shouldDirty: false,
                        });
                    });
                }

                if (controlProps.reloadGrids && controlProps.reloadGrids.length > 0) {
                    controlProps.reloadGrids.forEach((gridName: string) => {
                        queryClient.invalidateQueries({ queryKey: [gridName] });
                    });
                }
            })
            .finally(() => {
                toggleLoading(false);
                setShowConfirm(false);
            });
    };

    return (
        <>
            <Box alignContent={'end'} sx={{ marginLeft: '16px' }}>
                <Button
                    data-test={controlProps.uid}
                    onClick={handleClick}
                    variant={'outlined'}
                    startIcon={<IconMi icon={'refresh-cw'} />}
                    sx={{ maxHeight: '28px' }}
                >
                    {t(controlProps.label as string)}
                </Button>
            </Box>
            {isLoading && <LoadingPlaceholder />}
            {showConfirm && (
                <Popup
                    key={'variable-delete-confirm'}
                    settings={confirmSettings}
                    open={showConfirm}
                    onHide={() => setShowConfirm(false)}
                    onConfirm={onConfirmed}
                    maxWidth={'popupSm'}
                >
                    <Typography>
                        {isDirty
                            ? t('button_restore.confirmation_form_dirty_message')
                            : t('button_restore.confirmation_message')}
                    </Typography>
                </Popup>
            )}
        </>
    );
}

export class RestoreDefaultsFormButtonBuilder extends FormComponentBuilder {
    prepareProps(): RestoreDefaultsFormButtonProps {
        return {
            ...this.controlProps,
            url: this.elementProps.componentProps?.url ?? '',
            reloadGrids: this.elementProps.componentProps?.reloadGrids ?? [],
            reloadFields: this.elementProps.componentProps?.reloadFields ?? [],
        };
    }
}
